// ============================================
// src/app/pages/onboarding/models/onboarding.model.ts
// ============================================
// Modèles et interfaces pour l'onboarding

import type { ActivityType, Positioning } from './cluster.model';

// Re-export pour usage externe
export type { ActivityType, Positioning } from './cluster.model';

export interface CompleteOnboardingPayload {
  merchantType: MerchantType;
  activityType: ActivityType;
  affluence: Affluence;
  coverage: Coverage;
  // Champs optionnels selon le type de marchand
  eventTypes?: EventType[];
  periodicity?: Periodicity;
  services?: ServiceTime[];
  teamSize?: TeamSize;
  positioning?: Positioning;
}

/**
 * Type d'emplacement du point de vente
 * - fixed: stand permanent
 * - itinerant: marchés, festivals, événements ponctuels
 */
export enum MerchantType {
  FIXED = 'fixed',
  ITINERANT = 'itinerant',
}

/**
 * Niveau d'affluence estimée
 * - low: peu de clients réguliers
 * - medium: flux régulier
 * - high: pic de clients importants
 */
export enum Affluence {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

/**
 * Services de restauration pour marchands fixes
 * Noms français complets utilisés partout dans l'application
 */
export type ServiceTime = 'Petit-déjeuner' | 'Déjeuner' | 'Dîner';

/**
 * Périodicité des recommandations
 * - daily: quotidienne (recommandé)
 * - weekly: hebdomadaire
 * - seasonal: saisonnière
 */
export enum Periodicity {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  SEASONAL = 'seasonal',
}

/**
 * Types d'événements pour marchands itinérants
 */
export enum EventType {
  MARKET = 'market',
  FESTIVAL = 'festival',
  OFFICE_ZONE = 'office',
  OCCASIONAL = 'occasional',
  STREET = 'street',
  PRIVATE = 'private',
  OTHER = 'other',
}

/**
 * Type de couverture du point de vente
 * - covered: protection complète
 * - semi_covered: partiellement protégé
 * - uncovered: en plein air
 */
export enum Coverage {
  COVERED = 'covered',
  SEMI_COVERED = 'semi_covered',
  UNCOVERED = 'open',
}

/**
 * Taille de l'équipe
 * - solo: 1 personne
 * - two_persons: 2 personnes
 * - three_plus: 3+ personnes
 */
export enum TeamSize {
  SOLO = 'solo', // 1 personne
  TWO_PERSONS = '2persons', // 2 personnes
  THREE_PLUS = '3plus', // 3 personnes et plus
  MULTI_STAND = 'multi_stand', // plusieurs points de ventes
}

/**
 * Interface commune pour tous les marchands
 * Contient les données de base de l'onboarding
 */
export interface OnboardingData {
  merchantType: MerchantType;
  activityType: ActivityType;
}

/**
 * Interface pour les marchands FIXES
 * Étend OnboardingData avec des informations spécifiques aux points fixes
 */
export interface FixedMerchantData extends OnboardingData {
  // Contexte du point
  affluence: Affluence;
  coverage: Coverage;
  teamSize: TeamSize;

  // Services et périodicité
  services: ServiceTime[];
  periodicity: Periodicity;

  // Positionnement
  positioning?: Positioning;

  // Calculé automatiquement
  clusterId?: string; // Identifiant du cluster approprié
}

/**
 * Interface pour les marchands ITINÉRANTS
 * Étend OnboardingData avec des informations spécifiques aux itinérants
 */
export interface ItinerantMerchantData extends OnboardingData {
  // Contexte habituel
  eventTypes: EventType[];
  coverage: Coverage;
  teamSize: TeamSize;

  // Positionnement
  positioning?: Positioning;

  // Calculé automatiquement
  clusterId?: string; // Identifiant du cluster par défaut
}

/**
 * Interface pour les données secondaires (optionnelles)
 * À collecter progressivement après l'onboarding initial
 */
export interface SecondaryData {
  averageCustomersPerDay?: number;
  targetMarginPercent?: number;
  locationNotes?: string;
  clienteleType?: 'regular' | 'passing' | 'mixed';
  specialParticularities?: string;
}

/**
 * Interface pour un service (FIXE)
 * Représente un service régulier avec recommandation
 */
export interface Service {
  id: string;
  merchantId: string;
  date: string; // Format: YYYY-MM-DD
  dayOfWeek:
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday';
  service: ServiceTime;
  recommendedQuantity: number;
  confidence: number; // 0-100 (%)
  estimatedEconomy: number; // Économies estimées en €
  actualQuantity?: number; // Saisi après le service
  actualWaste?: number; // Gaspillage réel
  status: 'created' | 'completed' | 'skipped';
  createdAt: Date;
  updatedAt?: Date;
}

/**
 * Interface pour un événement (ITINÉRANT)
 * Représente un événement ponctuel avec recommandation
 */
export interface EventItinerant {
  id: string;
  merchantId: string;
  name: string; // Nom de l'événement
  date: string; // Format: YYYY-MM-DD
  eventType: EventType;
  affluence?: Affluence; // Affluence estimée pour cet événement
  coverage?: Coverage; // Couverture spécifique pour cet événement
  teamSize?: TeamSize; // Équipe spécifique pour cet événement
  recommendedQuantity: number;
  confidence: number; // 0-100 (%)
  estimatedEconomy: number; // Économies estimées en €
  actualQuantity?: number; // Saisi après l'événement
  actualWaste?: number; // Gaspillage réel
  status: 'created' | 'completed' | 'skipped';
  createdAt: Date;
  updatedAt?: Date;
}

/**
 * Interface pour les données de cluster
 * Contient les données de base pour les recommandations
 */
export interface ClusterData {
  id: string;
  merchantType: MerchantType;
  activityType: ActivityType;
  coverage: Coverage;
  teamSize: TeamSize;

  // Seulement pour FIXE
  services?: ServiceTime[];
  affluence?: Affluence;
  periodicity?: Periodicity;

  // Seulement pour ITINÉRANT
  eventTypes?: EventType[];

  // Données de base
  avgPreparedQuantity: number;
  avgSoldQuantity: number;
  avgWastePercent: number; // 0-100 (%)
  avgRuptureFrequency: number; // Nombre de ruptures par semaine
  maxDebitPerHour: number;

  // Répartition horaire
  hourlyDistribution: {
    hour: string; // "11:30-12:00"
    percentOfSales: number; // 0-100 (%)
  }[];

  // Ajustements
  weatherAdjustments: {
    sunny: number; // Multiplicateur
    veryHot: number;
    lightRain: number;
    heavyRain: number;
    cold: number;
    windy: number;
  };

  dayOfWeekAdjustments: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };

  // Économies
  costPerUnit: number; // Coût de matière première
  sellingPrice: number; // Prix de vente moyen
  grossMargin: number; // Marge brute

  // Valeurs économiques
  lostSaleValue: number; // Valeur d'une rupture (vente perdue)
  wasteValue: number; // Coût du gaspillage par unité
}

/**
 * Interface pour les économies estimées
 * Retournée après calcul des données du marchand
 */
export interface EstimatedEconomies {
  wasteReduction: number; // Euros par mois
  avoidedRuptures: number; // Euros par mois
  timeSaved: number; // Euros par mois (basé sur temps économisé)
  total: number; // Total mensualisé
  roi: number; // ROI (%) avec abonnement à 29.90€/mois
}

/**
 * Interface pour la réponse complète de completeOnboarding()
 * Retournée après succès de l'API
 */
export interface CompleteOnboardingResponse {
  // Données du marchand complétées
  merchant: {
    id: string;
    userId: string;
    merchantType: MerchantType;
    activityType: ActivityType;
    clusterId?: string;
    coverage?: Coverage;
    teamSize?: TeamSize;

    // FIXE
    affluence?: Affluence;
    services?: ServiceTime[];
    periodicity?: Periodicity;

    // ITINÉRANT
    eventTypes?: EventType[];

    onboardingCompleted: boolean;
    createdAt: Date;
  };

  // Premier service (FIXE)
  firstService?: Service;

  // Premier événement (ITINÉRANT)
  firstEvent?: EventItinerant;

  // Produits par défaut (optionnel)
  defaultProducts?: {
    id: string;
    name: string;
    category: string;
    sellPrice: number;
  }[];

  // Auth
  token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

/**
 * Interface pour l'état global de l'onboarding
 * Utilisée par OnboardingStateService
 */
export interface OnboardingState {
  currentStep: 1 | 2 | 3 | 4 | 5 | 6; // Numéro d'étape actuelle
  merchantType?: MerchantType | null; // Déterminé à l'étape 1
  formData: {
    activityType?: ActivityType;
    affluence?: Affluence;
    coverage?: Coverage;
    teamSize?: TeamSize;
    services?: ServiceTime[];
    periodicity?: Periodicity;
    eventTypes?: EventType[];
    positioning?: Positioning;
  };
  isStepComplete: boolean;
  isLoading: boolean;
  error?: string;
}
